import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';
import debounce from 'lodash/debounce';

import Badge from 'src/components/ui//Badge';
import Typography from 'src/components/ui/Typography';
import type { ExtendedAdsQueryVariables } from 'src/components/ad/SearchAdsPage';
import { SaveSearchModal } from 'src/components/ad/SearchAdsPage/view/SaveSearchModal';
import LinkButton from 'src/components/ui/LinkButton';

import useModalOpenState from 'src/hooks/useModalOpenState';

import { styledComponent } from 'src/utils/styled';
import theme from 'src/utils/theme';
import formatPrice from 'src/utils/formatPrice';
import { AnalyticsEvent } from 'src/utils/analytics';

import { housingTypes } from 'src/constants/searchAdsTypes';

import type { SavedSearchesResponseWithTotal } from 'src/types/__generated__/graphql';
import { NotificationSubscriptionStatus } from 'src/types/__generated__/graphql';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { useAdsTotalCount } from '../hooks/useAdsTotalCount';
import { useSavedSearchAdsCountSinceLastViewed } from '../hooks/useSavedSearchAdsCountSinceLastViewed';
import Divider from 'src/components/ui/Divider';
import { Switch } from 'src/components/ui/Switch';
import { useUserNotificationSubscriptions } from 'src/hooks/useUserNotificationSubscriptions';
import { useSavedSearchMutations } from '../hooks/useSavedSearchMutations';

const ItemContainer = styledComponent(Box)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 0.8rem;
`;

const BlueLinkButton = styledComponent(LinkButton)`
    color: ${theme.palette.secondary.main};
    text-decoration: none;
    transition: text-decoration 0.3s ease;
    &:hover {
        text-decoration: underline;
    }
    font-size: 0.875rem;
`;

const TotalResultsTypography = styledComponent(Typography)`
    font-size: 0.8125rem;
    line-height: 1.8;
`;

interface Props {
    search: ArrayElement<SavedSearchesResponseWithTotal['results']>;
    onItemClick: () => void;
    onDeleteClick: () => void;
}

export const SavedSearchItem: React.FC<Props> = ({ search, onItemClick, onDeleteClick }) => {
    const { t, i18n } = useTranslation(['common', 'ad', 'notifications']);
    const userNotificationSubscriptions = useUserNotificationSubscriptions();
    const { logEventDynamic } = useDynamicLogEvents();

    const { props: saveSearchModalProps, open: openSaveSearchModal } = useModalOpenState();
    const parsedSearchParams = JSON.parse(search.parameters) as ExtendedAdsQueryVariables;

    const { updateSavedSearch } = useSavedSearchMutations();
    const { savedSearchAdsCountSinceLastViewed } = useSavedSearchAdsCountSinceLastViewed({
        searchId: search.id,
    });
    const { adsTotalCount } = useAdsTotalCount({
        placeGeometry: search.placeGeometry ?? undefined,
        parameters: parsedSearchParams,
    });

    const handleUpdateNotificationEnabled = debounce(
        (emailNotificationsEnabled: boolean) =>
            updateSavedSearch({
                id: search.id,
                emailNotificationsEnabled,
            }),
        250,
    );

    const subtitle = useMemo(() => {
        const { place } = JSON.parse(search.parameters) as ExtendedAdsQueryVariables;
        const placeDescription = place?.cityName ?? place?.description;
        let name = placeDescription ? `${placeDescription} · ` : '';
        if (parsedSearchParams.type === 'sell') {
            name += t('ad:purchase');
        } else {
            name += t('ad:rental');
        }
        housingTypes.forEach((type) => {
            if ((parsedSearchParams as Record<string, boolean>)[type]) {
                name += ` · ${t(`ad:propertyTypes.${type}`)}`;
            }
        });
        return name;
    }, [parsedSearchParams, search.parameters, t]);

    const priceSpread = useMemo(() => {
        const minPart = parsedSearchParams.minPrice
            ? formatPrice({
                  price: parsedSearchParams.minPrice,
                  lang: i18n.language,
              })
            : 'Min';
        const maxPart = parsedSearchParams.maxPrice
            ? String(
                  formatPrice({
                      price: parsedSearchParams.maxPrice,
                      lang: i18n.language,
                  }),
              )
            : 'Max';
        return minPart !== 'Min' || maxPart !== 'Max' ? `${minPart} - ${maxPart}` : '';
    }, [i18n.language, parsedSearchParams.maxPrice, parsedSearchParams.minPrice]);

    return (
        <ItemContainer>
            <Box display="flex" flexDirection="row" alignItems="center" width="100%">
                <Box display="flex" flexDirection="column" width="100%">
                    <Box
                        margin="0.9rem"
                        marginBottom="0.5rem"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            logEventDynamic(AnalyticsEvent.SavedSearchSelected);
                            onItemClick();
                        }}
                    >
                        <Box display="flex" flexDirection="row" justifyContent="space-between">
                            <Typography variant="h5" style={{ fontWeight: 600 }} noWrap>
                                {search.name}
                            </Typography>
                            <TotalResultsTypography>
                                {t('common:totalResults', {
                                    total: String(Intl.NumberFormat(i18n.language).format(adsTotalCount ?? 0)),
                                    plural: (adsTotalCount ?? 0) > 1 ? 's' : '',
                                })}
                            </TotalResultsTypography>
                        </Box>
                        <Typography variant="subtitle2" marginTop="0.2rem" noWrap>
                            {subtitle}
                        </Typography>
                        <Typography variant="subtitle2" noWrap>
                            {priceSpread}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" flexDirection="row" marginX="0.9rem">
                        <BlueLinkButton
                            onClick={(e) => {
                                e.stopPropagation();
                                openSaveSearchModal();
                            }}
                            mr="0.5rem"
                        >
                            {t('common:rename')}
                        </BlueLinkButton>
                        <Typography color={theme.palette.secondary.main}>·</Typography>
                        <BlueLinkButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick();
                            }}
                            ml="0.5rem"
                        >
                            {t('common:delete')}
                        </BlueLinkButton>
                    </Box>
                    <Divider sx={{ marginTop: '0.75rem' }} />
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        marginX="0.9rem"
                        marginY="0.5rem"
                    >
                        <Typography>{t('notifications:receiveNewPropertyByEmail')}</Typography>
                        <Switch
                            value={search.emailNotificationsEnabled}
                            disabled={
                                userNotificationSubscriptions?.savedSearch.emailNotificationsStatus ===
                                NotificationSubscriptionStatus.Inactive
                            }
                            onChange={handleUpdateNotificationEnabled}
                            container={false}
                        />
                    </Box>
                </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center">
                {(savedSearchAdsCountSinceLastViewed ?? 0) > 0 && (
                    <Box mt="1rem" ml="-1rem" height="2rem">
                        <Badge color="tertiary" badgeContent={savedSearchAdsCountSinceLastViewed} />
                    </Box>
                )}
            </Box>
            {saveSearchModalProps.open && (
                <SaveSearchModal {...saveSearchModalProps} showEmailNotificationsOption={false} savedSearch={search} />
            )}
        </ItemContainer>
    );
};
